import { Grid } from '@mui/material';
import { dateFormatShortDate } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { Masonry } from 'masonic';
import { useNavigate } from 'react-router-dom';

import BackArrowIcon from '../../../assets/svg/BackArrowIcon';
import { RatingStars } from '../../../components/RatingStars';
import * as routing from '../../../constants/Routing';
import { AnalyticsCustomersSurvey } from '../../../domain/analytics/AnalyticsCustomersSurvey';
import { AnalyticsDriversSurvey } from '../../../domain/analytics/AnalyticsDriversSurvey';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../react-components/Text';
import {
  getSurveyRatingIcon as getDriverSurveyRatingIcon,
  getSurveyRatingTagText as getDriverSurveyRatingTagText,
} from '../../planner/shared/DriverRatingTag';
import { getStopSurveyRatingIcon, getStopSurveyRatingTagText } from '../../planner/shared/StopRatingTag';
import StopCircleColor from '../../shared/StopCircleColor';
import { VehicleAvatar } from '../../shared/VehicleAvatar';
interface AnalyticsCustomersMasonryCardProps {
  index: number;
  review: AnalyticsCustomersSurvey;
  projectId: string;
}

const AnalyticsCustomersMasonryCard = ({ index, review, projectId }: AnalyticsCustomersMasonryCardProps) => {
  const navigate = useNavigate();
  const RatingTagIcon = getStopSurveyRatingIcon({ rating: review.rating, ratingTag: review.rating_tag });

  return (
    <Grid
      key={index}
      container
      direction="column"
      style={{
        backgroundColor: `white`,
        padding: `16px 14px`,
        borderRadius: `2px`,
        border: `1px solid #E0E0E0`,
        position: `relative`,
        minHeight: `134px`,
        color: `#ACA7A7`,
      }}
    >
      <Grid container direction="row" wrap="nowrap" justifyContent="center" style={{ position: `relative` }}>
        <StopCircleColor
          number={review.stop_order}
          color={review.vehicle_color?.value}
          // isPickup={review.stop_type === `pickup`}
          size={40}
        />

        {RatingTagIcon ? (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            justifyContent="flex-end"
            style={{ position: `absolute`, right: 0, top: `-6px` }}
          >
            <RoutalTooltip
              title={intl.formatMessage({
                id: getStopSurveyRatingTagText({ rating: review.rating, ratingTag: review.rating_tag }),
              })}
              overlayInnerStyle={{
                color: RoutalPalette.neutral00,
              }}
            >
              <RatingTagIcon style={{ width: `24px`, color: `#AAAAAA` }} />
            </RoutalTooltip>
          </Grid>
        ) : null}
      </Grid>

      <VSpacer medium />

      <Text variant="h6" style={{ textAlign: `center`, fontWeight: 600 }}>
        {review.stop_label}
      </Text>

      <VSpacer small />

      <RatingStars rating={review.rating ?? 0} justifyContent="center" />

      <VSpacer medium />

      <Text style={{ wordBreak: `break-word` }}>{review.comments ?? ``}</Text>

      <VSpacer medium />

      <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
        <Text variant="label">{dateFormatShortDate(review.created_at)}</Text>

        <Grid
          container
          alignItems="center"
          style={{ cursor: `pointer`, width: `unset` }}
          onClick={() => {
            navigate(
              `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, projectId).replace(
                `:planId`,
                review.plan_id
              )}?detailType=stop&detailId=${review.stop_id}`
            );
          }}
        >
          <BackArrowIcon
            style={{
              color: RoutalPalette.primary40,
              transform: `rotate(180deg)`,
              fontSize: `16px`,
              marginRight: `4px`,
            }}
          />
          <Text variant="button" intlId="project_analytics.customers.satisfaction.reviews.go_to_plan" />
        </Grid>
      </Grid>
    </Grid>
  );
};

interface AnalyticsDriversMasonryCardProps {
  index: number;
  review: AnalyticsDriversSurvey;
  projectId: string;
}

const AnalyticsDriversMasonryCard = ({ index, review, projectId }: AnalyticsDriversMasonryCardProps) => {
  const navigate = useNavigate();
  const RatingTagIcon = getDriverSurveyRatingIcon({ rating: review.rating, ratingTag: review.rating_tag });

  return (
    <Grid
      key={index}
      container
      direction="column"
      style={{
        backgroundColor: `white`,
        padding: `16px 14px`,
        borderRadius: `2px`,
        border: `1px solid #E0E0E0`,
        position: `relative`,
        minHeight: `134px`,
        color: `#ACA7A7`,
      }}
    >
      <Grid container direction="row" wrap="nowrap" justifyContent="center" style={{ position: `relative` }}>
        <VehicleAvatar
          vehicleId={review.vehicle_id!}
          vehicleColor={review.vehicle_color?.value}
          additionalStyles={{
            width: `40px`,
            height: `40px`,
          }}
        />

        {RatingTagIcon ? (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            justifyContent="flex-end"
            style={{ position: `absolute`, right: 0, top: `-6px` }}
          >
            <RoutalTooltip
              title={intl.formatMessage({
                id: getDriverSurveyRatingTagText({ rating: review.rating, ratingTag: review.rating_tag }),
              })}
              overlayInnerStyle={{
                color: RoutalPalette.neutral00,
              }}
            >
              <RatingTagIcon style={{ width: `24px`, color: `#AAAAAA` }} />
            </RoutalTooltip>
          </Grid>
        ) : null}
      </Grid>

      <VSpacer medium />

      <Text variant="h6" style={{ textAlign: `center`, fontWeight: 600 }}>
        {review.driver_label}
      </Text>

      <VSpacer small />

      <RatingStars rating={review.rating ?? 0} justifyContent="center" />

      <VSpacer medium />

      <Text style={{ wordBreak: `break-word` }}>{review.comments ?? ``}</Text>

      <VSpacer medium />

      <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
        <Text variant="label">{dateFormatShortDate(review.created_at)}</Text>

        <Grid
          container
          alignItems="center"
          style={{ cursor: `pointer`, width: `unset` }}
          onClick={() => {
            navigate(
              `${routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, projectId).replace(
                `:planId`,
                review.plan_id
              )}?detailType=driver&detailId=${review.driver_id}`
            );
          }}
        >
          <BackArrowIcon
            style={{
              color: RoutalPalette.primary40,
              transform: `rotate(180deg)`,
              fontSize: `16px`,
              marginRight: `4px`,
            }}
          />
          <Text variant="button" intlId="project_analytics.drivers.satisfaction.reviews.go_to_plan" />
        </Grid>
      </Grid>
    </Grid>
  );
};

interface AnalyticsMasonryProps {
  key: string;
  type: `drivers` | `customers`;
  items: Array<AnalyticsDriversSurvey | AnalyticsCustomersSurvey>;
}

const AnalyticsMasonry = ({ key, items, type }: AnalyticsMasonryProps) => {
  const { data: activeProject } = useRetrieveActiveProject();

  if (!activeProject) {
    return null;
  }

  return (
    <Masonry
      key={key}
      items={items}
      columnCount={5}
      columnWidth={250}
      columnGutter={10}
      rowGutter={10}
      itemHeightEstimate={134}
      overscanBy={Infinity}
      itemKey={(data) => `${data.driver_id}${data.plan_id}${data.created_at}`}
      render={({ index, data: review }) => {
        switch (type) {
          case `drivers`:
            return (
              <AnalyticsDriversMasonryCard
                index={index}
                review={review as AnalyticsDriversSurvey}
                projectId={activeProject.id}
              />
            );
          case `customers`:
            return (
              <AnalyticsCustomersMasonryCard
                index={index}
                review={review as AnalyticsCustomersSurvey}
                projectId={activeProject.id}
              />
            );
          default:
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <></>;
        }
      }}
    />
  );
};

export default AnalyticsMasonry;
